import {
  DeleteObjectCommand,
  GetObjectCommand,
  S3Client,
} from "@aws-sdk/client-s3";
import { Progress, Upload } from "@aws-sdk/lib-storage";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { getCloudflareContext } from "@opennextjs/cloudflare";

/**
 * S3 client instance configured for Cloudflare R2 storage
 */
const s3Client = new S3Client({
  region: "auto",
  endpoint: `https://${process.env.CLOUDFLARE_ACCOUNT_ID}.r2.cloudflarestorage.com`,
  credentials: {
    accessKeyId: process.env.CLOUDFLARE_R2_APP_ASSETS_ACCESS_KEY_ID!,
    secretAccessKey: process.env.CLOUDFLARE_R2_APP_ASSETS_SECRET_ACCESS_KEY!,
  },
});

/**
 * Generates a signed URL for a file in the S3/R2 bucket
 *
 * @param {string} fileName - The name of the file to generate a signed URL for
 * @returns {Promise<string>} A signed URL that provides temporary access to the file
 */
export const getSignedUrlUtil = async (fileName: string) => {
  const command = new GetObjectCommand({
    Bucket: process.env.CLOUDFLARE_R2_APP_ASSETS_BUCKET_NAME!,
    Key: fileName,
  });
  const signedUrl = await getSignedUrl(s3Client, command, { expiresIn: 3600 });
  return signedUrl;
};

/**
 * Generates a public URL for a file in the S3/R2 bucket
 *
 * @param {string} fileName - The name of the file to generate a URL for
 * @returns {Promise<string>} A public URL for accessing the file
 */
export const getUrlUtil = async (fileName: string) => {
  return `https://${process.env.CLOUDFLARE_R2_APP_ASSETS_BUCKET_DOMAIN}/${fileName}`;
};

/**
 * Uploads a file to the S3/R2 bucket with progress tracking
 *
 * @param {Buffer} file - The file buffer to upload
 * @param {string} fileName - The name to give the file in storage
 * @param {string} contentType - The MIME type of the file
 * @returns {Promise<string>} The public URL of the uploaded file
 * @throws {Error} If the upload fails
 */
export const uploadFileToBlobStorage = async (
  file: Buffer,
  fileName: string,
  contentType: string,
) => {
  const context = getCloudflareContext();
  // @ts-ignore
  const bucket = context.env.ASSETS_BUCKET;
  const directory = process.env.CLOUDFLARE_R2_APP_ASSETS_BUCKET_DIRECTORY!;
  const filePath = `${directory}/${fileName}`;

  console.log(`Starting R2 upload for ${filePath}:`, {
    fileSize: `${(file.length / 1024 / 1024).toFixed(2)}MB`,
    contentType,
  });

  await bucket.put(filePath, file, {
    httpMetadata: {
      contentType,
    },
  });
  console.log(`R2 upload completed for ${filePath}:`);
  const url = await getUrlUtil(filePath);
  console.log("url", url);
  return url;
};

/**
 * Deletes a file from the S3/R2 bucket
 *
 * @param {string} fileName - The name of the file to delete
 * @returns {Promise<void>} A promise that resolves when the file is deleted
 * @throws {Error} If the deletion fails
 */
export const deleteFileFromBlobStorage = async (fileName: string) => {
  const context = getCloudflareContext();
  // @ts-ignore
  const bucket = context.env.ASSETS_BUCKET;
  const directory = process.env.CLOUDFLARE_R2_APP_ASSETS_BUCKET_DIRECTORY!;
  const filePath = `${directory}/${fileName}`;
  await bucket.delete(filePath);
  console.log(`R2 delete completed for ${filePath}:`);
};

/**
 * Uploads a file to the S3/R2 bucket with progress tracking
 *
 * @param {Buffer} file - The file buffer to upload
 * @param {string} fileName - The name to give the file in storage
 * @param {string} contentType - The MIME type of the file
 * @returns {Promise<string>} The public URL of the uploaded file
 * @throws {Error} If the upload fails
 */
export const uploadFileToBlobStorageViaS3 = async (
  file: Buffer,
  fileName: string,
  contentType: string,
) => {
  const directory = process.env.CLOUDFLARE_R2_APP_ASSETS_BUCKET_DIRECTORY!;
  const filePath = `${directory}/${fileName}`;

  console.log(`Starting upload for ${filePath}:`, {
    fileSize: `${(file.length / 1024 / 1024).toFixed(2)}MB`,
    contentType,
  });

  const Key = filePath;
  const Bucket = process.env.CLOUDFLARE_R2_APP_ASSETS_BUCKET_NAME!;

  try {
    const upload = new Upload({
      client: s3Client,
      params: {
        Bucket,
        Key,
        Body: file,
        ContentType: contentType,
      },
      queueSize: 2,
      partSize: 5 * 1024 * 1024, // 5MB parts
      leavePartsOnError: false,
    });

    let lastProgress = 0;
    upload.on("httpUploadProgress", (progress: Progress) => {
      const percentComplete = Math.round(
        ((progress.loaded ?? 0) / (progress.total ?? 1)) * 100,
      );
      if (percentComplete !== lastProgress) {
        console.log(`Upload progress for ${filePath}: ${percentComplete}%`);
        lastProgress = percentComplete;
      }
    });

    const res = await upload.done();
    console.log(`Upload completed for ${filePath}:`, res);

    const imageUrl = await getUrlUtil(filePath);
    return imageUrl;
  } catch (error: any) {
    console.error(`Error uploading file ${filePath}:`, {
      error,
      errorName: error.name,
      errorMessage: error.message,
      fileSize: `${(file.length / 1024 / 1024).toFixed(2)}MB`,
      contentType,
    });
    throw error;
  }
};

/**
 * Deletes a file from the S3/R2 bucket
 *
 * @param {string} fileName - The name of the file to delete
 * @returns {Promise<any>} The response from the delete operation
 * @throws {Error} If the deletion fails
 */
export const deleteFileFromBlobStorageViaS3 = async (fileName: string) => {
  const directory = process.env.CLOUDFLARE_R2_APP_ASSETS_BUCKET_DIRECTORY!;
  const filePath = `${directory}/${fileName}`;
  const Key = filePath;
  const Bucket = process.env.CLOUDFLARE_R2_APP_ASSETS_BUCKET_NAME!;
  let res;

  try {
    res = await s3Client.send(
      new DeleteObjectCommand({
        Bucket,
        Key,
      }),
    );
  } catch (error) {
    console.error("Error deleting file: ", error);
    throw error;
  }
  return res;
};
